// client/src/pages/Manage/index.jsx
/**
 * Page /manage — accueil après connexion admin/manager sur le serveur.
 * Choix : « Audit » (activité des guichetiers) ou « Config » (connexion au central).
 * @author Dr Hamid MADANI <drmdh@msn.com>
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/ConfigContext';

function Manage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  // Chaque tuile porte une CLÉ stable : la console centrale peut la MASQUER une par une
  // via `config.manageMenu.<clé> === true` (défaut absent = visible). `changePassword` est
  // volontairement NON masquable (un utilisateur doit toujours pouvoir changer son mot de passe).
  const menu = config?.manageMenu || {};
  const allTiles = [
    { key: 'audit', to: '/audit', title: 'Audit', desc: "Journal d'activité des guichetiers", icon: '📋' },
    { key: 'centralConfig', to: '/config', title: 'Config', desc: 'Connexion au central (token, identifiant)', icon: '⚙️' },
    { key: 'sound', to: '/sound', title: 'Son & Annonces', desc: 'Bip d\'appel, annonces vocales (AR/FR/EN), voix', icon: '🔊' },
    { key: 'numbering', to: '/numbering', title: 'Numérotation', desc: 'N° d\'ordre global, position « ~N avant vous » sur le ticket', icon: '🔢' },
    { key: 'displays', to: '/displays', title: 'Affichages', desc: 'Écrans master/slave, filtre de services (piloté à distance)', icon: '📺' },
    { key: 'ticker', to: '/ticker', title: 'Bandelette', desc: 'Message défilant sur la borne : texte, sens, couleurs', icon: '📢' },
    { key: 'changePassword', to: '/change-password', title: 'Mot de passe', desc: 'Changer le mot de passe de votre compte', icon: '🔑', locked: true },
    { key: 'backup', to: '/backup', title: 'Sauvegardes', desc: 'Planifier, vérifier et RESTAURER la base', icon: '💾' },
  ];
  // Masquable = tout sauf les tuiles `locked`. Coché (=== true) → masqué ; défaut → visible.
  const tiles = allTiles.filter((t) => t.locked || menu[t.key] !== true);

  // Navigation vers les pages de l'app — en phase avec le menu « Pages » de l'app Electron
  // (main.js). L'entrée « Administration (legacy) → /admin » est volontairement écartée :
  // la route legacy a été retirée (elle retombe sur 404), voir App.jsx.
  const pages = [
    { to: '/display', title: 'Affichage Client', desc: "Écran d'affichage de la salle d'attente", icon: '📺' },
    { to: '/distributor', title: 'Distributeur Tickets', desc: 'Borne de distribution des tickets', icon: '🎫' },
    { to: '/counter', title: 'Guichetier', desc: 'Poste de service au guichet', icon: '🧑‍💼' },
    { to: '/setup', title: 'Installation', desc: "Assistant d'installation / configuration initiale", icon: '🛠️' },
  ];

  const runAction = async (key, url, confirmMsg, okMsg) => {
    if (!window.confirm(confirmMsg)) return;
    setBusy(key); setMsg('');
    try {
      const r = await axios.post(url);
      const cleared = r?.data?.data?.cleared;
      setMsg(`${okMsg}${cleared != null ? ` (${cleared})` : ''}`);
    } catch (e) {
      setMsg(`Échec : ${e?.response?.data?.error || e?.message || e}`);
    } finally { setBusy(''); }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6 md:p-10">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Administration TicketFlow</h1>
            <p className="text-sm text-gray-500">{user?.fullName || user?.username} · {user?.role}</p>
          </div>
          <button onClick={() => { logout(); navigate('/login'); }} className="text-sm text-gray-600 hover:text-gray-900 underline">Déconnexion</button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {tiles.map((tile) => (
            <button
              key={tile.to}
              onClick={() => navigate(tile.to)}
              className="text-left bg-white rounded-xl shadow hover:shadow-md transition p-6 border border-gray-100"
            >
              <div className="text-4xl mb-3">{tile.icon}</div>
              <div className="text-lg font-semibold text-gray-800">{tile.title}</div>
              <div className="text-sm text-gray-500 mt-1">{tile.desc}</div>
            </button>
          ))}
        </div>

        {/* Pages de l'app — mêmes destinations que le menu « Pages » de l'app Electron. */}
        <div className="mt-8">
          <div className="text-lg font-semibold text-gray-800 mb-3">Pages</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {pages.map((p) => (
              <button
                key={p.to}
                onClick={() => navigate(p.to)}
                title={p.desc}
                className="text-center bg-white rounded-xl shadow hover:shadow-md transition p-4 border border-gray-100"
              >
                <div className="text-3xl mb-2">{p.icon}</div>
                <div className="text-sm font-semibold text-gray-800">{p.title}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-white rounded-xl shadow p-6 border border-gray-100">
          <div className="text-lg font-semibold text-gray-800 mb-1">Gestion de la file</div>
          <div className="text-sm text-gray-500 mb-4">Réinitialisation des compteurs (statistiques) et purge de la file d'attente.</div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => runAction('reset', '/api/counters/reset', 'Réinitialiser les compteurs du jour maintenant ?', '✓ Compteurs réinitialisés')}
              disabled={busy === 'reset'}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {busy === 'reset' ? '…' : '↻ Réinitialiser les compteurs'}
            </button>
            <button
              onClick={() => runAction('clear', '/api/counters/clear-queue', 'Annuler TOUS les tickets en attente et en cours ?', '✓ File vidée')}
              disabled={busy === 'clear'}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {busy === 'clear' ? '…' : '🗑 Vider la file d\'attente'}
            </button>
          </div>
          {msg && <div className="mt-3 text-sm text-gray-700">{msg}</div>}
        </div>
      </div>
    </div>
  );
}

export default Manage;
